import type {
	ContainerBackgroundSettings,
	ContainerBackgroundSize,
} from "../types";

const IMAGE_MEDIA_UUID_RE =
	/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function normalizeContainerBackgroundOpacity(
	pct: number | undefined,
	fallback: number
): number {
	if (typeof pct !== "number" || Number.isNaN(pct)) {
		return fallback;
	}
	return Math.min(100, Math.max(0, Math.round(pct)));
}

export function normalizeContainerBackgroundSize(
	value: string | undefined
): ContainerBackgroundSize {
	if (value === "contain" || value === "auto") {
		return value;
	}
	return "cover";
}

function clamp01(n: number): number {
	return Math.min(1, Math.max(0, n));
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
	const t = hex.trim().replace(/^#/, "");
	if (!/^[0-9a-f]{3}$|^[0-9a-f]{6}$/i.test(t)) {
		return null;
	}
	const full =
		t.length === 3
			? t
					.split("")
					.map((c) => c + c)
					.join("")
			: t;
	const n = Number.parseInt(full, 16);
	return {
		r: Math.floor(n / 65_536) % 256,
		g: Math.floor(n / 256) % 256,
		b: n % 256,
	};
}

/** Build an rgba() overlay from a color token and 0–100 opacity percent. */
export function containerOverlayRgba(
	color: string,
	opacityPct: number
): string {
	const trimmed = color.trim();
	if (!trimmed) {
		return "transparent";
	}
	const pct = normalizeContainerBackgroundOpacity(opacityPct, 0);
	if (pct <= 0) {
		return "transparent";
	}
	const alpha = clamp01(pct / 100);

	const rgbaMatch = /^rgba?\(\s*([^)]+)\)$/i.exec(trimmed);
	if (rgbaMatch) {
		const parts = rgbaMatch[1].split(",").map((p) => p.trim());
		if (parts.length >= 3) {
			return `rgba(${parts[0]}, ${parts[1]}, ${parts[2]}, ${alpha})`;
		}
	}

	if (trimmed.startsWith("#")) {
		const rgb = hexToRgb(trimmed);
		if (rgb) {
			return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
		}
	}

	if (trimmed.startsWith("var(")) {
		return `color-mix(in srgb, ${trimmed} ${pct}%, transparent)`;
	}

	return trimmed;
}

export function resolveContainerBackgroundImageUrl(
	mediaId: string | undefined,
	resolvedUrl?: string | null
): string {
	const resolved = resolvedUrl?.trim();
	if (resolved) {
		return resolved;
	}
	const id = mediaId?.trim() ?? "";
	if (!id) {
		return "";
	}
	if (IMAGE_MEDIA_UUID_RE.test(id)) {
		return `/api/media/${id}?content=1&variant=large`;
	}
	if (id.startsWith("/") || /^https?:\/\//i.test(id)) {
		return id;
	}
	return "";
}

export function hasContainerBackgroundLayers(
	background: ContainerBackgroundSettings | undefined
): boolean {
	if (!background) {
		return false;
	}
	const imageUrl = resolveContainerBackgroundImageUrl(background.mediaId);
	const overlayColor = background.overlayColor?.trim();
	const overlayOpacity = normalizeContainerBackgroundOpacity(
		background.overlayOpacity,
		0
	);
	return Boolean(imageUrl) || Boolean(overlayColor && overlayOpacity > 0);
}

export function parseContainerBackgroundSettings(
	raw: unknown
): ContainerBackgroundSettings | undefined {
	if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
		return undefined;
	}
	const o = raw as Record<string, unknown>;
	const out: ContainerBackgroundSettings = {};

	if (typeof o.mediaId === "string" && o.mediaId.trim()) {
		out.mediaId = o.mediaId.trim();
	}
	if (typeof o.imageOpacity === "number" && !Number.isNaN(o.imageOpacity)) {
		out.imageOpacity = normalizeContainerBackgroundOpacity(o.imageOpacity, 100);
	}
	if (typeof o.overlayColor === "string" && o.overlayColor.trim()) {
		out.overlayColor = o.overlayColor.trim();
	}
	if (typeof o.overlayOpacity === "number" && !Number.isNaN(o.overlayOpacity)) {
		out.overlayOpacity = normalizeContainerBackgroundOpacity(
			o.overlayOpacity,
			0
		);
	}
	if (typeof o.size === "string" && o.size.trim()) {
		out.size = normalizeContainerBackgroundSize(o.size);
	}
	if (typeof o.position === "string" && o.position.trim()) {
		out.position = o.position.trim();
	}

	return Object.keys(out).length > 0 ? out : undefined;
}
